import { Injectable, OnDestroy } from '@angular/core';

import { Subscription, BehaviorSubject, Observable, Subject, from } from 'rxjs';
import { catchError, mergeMap, share, switchMap, filter, take, map, tap } from 'rxjs/operators';
import { MessageService } from 'primeng/api';

import { ContactsPublicApi } from '../../../api/targeting/contacts-public.api';
import { ContactModel } from '../model/contact.model';

import { ConfirmDialogModel } from '../../../Common/Dialogs/models/confirm-dialog.model';
import { CoreDialogService } from '../../../Core/Dialogs/services/dialog.service';
import { ConfirmDialogComponent } from '../../../Common/Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { DiscoverRoutingService } from '../../../Discover/DiscoverRouter/services/discover-routing.service';
import { TargetingList } from '../../../Discover/Targeting/models/targeting-list.model';
import { TargetingListsApiService } from '../../../api/targeting/targeting-lists.api.service';
import { TargetingSearchService } from '../../../Discover/Targeting/services/targeting-search.service';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { TargetingResultModel } from '../../../Discover/Targeting/models/targeting-result.model';
import { messageSeverityEnum } from '../../../Common/Misc/enums/message-severity.enum';
import { TargetingQueryApi } from '../../../api/targeting/model/targeting-search.api.model';
import { EntityType } from '../../../api/targeting/enum/entity.type.enum';
import { BaseJournodexEntityModel } from '../../../Discover/Targeting/models/base-journodex-entity.model';
import { OutletModel } from '../../../Discover/Targeting/models/outlet.model';
import { OutletsPublicApi } from '../../../api/targeting/outlets-public.api';
import { PrivateDataModel } from '../../../Discover/Targeting/models/private-data.model';
import { NotesAllLayerUpdate } from '../../../Common/TargetingShared/components/UserNotes/user-notes.component';
import { JournodexEntityFactory } from '../../../Discover/Targeting/factories/journodex-entity.factory';
import { TargetingResultFactory } from '../../../Discover/Targeting/factories/targeting-result.factory';
import { HandleModel } from '../../../Discover/Targeting/models/handle.model';
import { TargetingListService } from '../../../Discover/Targeting/services/targeting-list.service';

export interface PrivateDataAllLayerUpdate {
    entityId: string;
    entityType: EntityType;
    privateData: PrivateDataModel;
}

export interface HandlesAllLayerUpdate {
    entityId: string;
    entityType: EntityType;
    handles: HandleModel[];
}

@Injectable()
export class ContactService implements OnDestroy {

    private subscriptions: Subscription[] = [];

    private editingContact: ContactModel;
    private editingContact$: BehaviorSubject<ContactModel> = new BehaviorSubject<ContactModel>(null);
    private originalContact: ContactModel;

    private newPrivateContact: TargetingResultModel;
    private newPrivateContact$: Subject<TargetingResultModel> = new Subject();

    private rhpFocusedEntity: TargetingResultModel;
    private rhpFocusedEntity$: BehaviorSubject<TargetingResultModel> = new BehaviorSubject<TargetingResultModel>(null);
    protected fullEntityRequest: Subscription;

    private rhpProfileEntity: ContactModel | OutletModel;
    private rhpProfileEntity$: BehaviorSubject<BaseJournodexEntityModel> = new BehaviorSubject<BaseJournodexEntityModel>(null);

    private privateDataAllLayerUpdate: PrivateDataAllLayerUpdate;
    private privateDataAllLayerUpdate$: BehaviorSubject<PrivateDataAllLayerUpdate> = new BehaviorSubject<PrivateDataAllLayerUpdate>(null);

    private dirtyFlag: boolean = false;
    private dirtyFlag$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(this.dirtyFlag);

    private deleteLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    private notesAllLayerUpdate$: Subject<NotesAllLayerUpdate> = new Subject<NotesAllLayerUpdate>();

    private handlesAllLayerUpdate: HandlesAllLayerUpdate;
    private handlesAllLayerUpdate$: BehaviorSubject<HandlesAllLayerUpdate> = new BehaviorSubject<HandlesAllLayerUpdate>(null);

    private updateEditingContactSubject = new Subject<string>();
    private updateEditingContact$ = this.updateEditingContactSubject.pipe(
        switchMap(uuid => this.getAndUpdateEditingContact(uuid)),
        share()
    );

    constructor(
        private contactsPublicApi: ContactsPublicApi,
        private outletsPublicApi: OutletsPublicApi,
        private messageService: MessageService,
        private coreDialogService: CoreDialogService,
        private discoverRoutingService: DiscoverRoutingService,
        private targetingListsApiService: TargetingListsApiService,
        private targetingSearchService: TargetingSearchService,
        private localizationService: LocalizationService,
        private journodexEntityFactory: JournodexEntityFactory,
        private targetingResultFactory: TargetingResultFactory,
        private targetingListService: TargetingListService,
    ) {
        this.subscriptions.push(
            this.updateEditingContact$.subscribe(),
        );
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
        this.subscriptions = [];
    }

    getDeleteLoading(): Observable<boolean> {
        return this.deleteLoading$;
    }

    createBlank(): void {
        this.editingContact = this.journodexEntityFactory.createEmptyContact();
        this.originalContact = this.editingContact.clone();
        this.editingContact$.next(this.editingContact);
    }

    clearEditingContact(): void {
        this.editingContact = null;
        this.editingContact$.next(this.editingContact);
    }

    getNewPrivateContact(): Observable<TargetingResultModel> {
        return this.newPrivateContact$;
    }

    getEditingContact(): Observable<ContactModel> {
        return this.editingContact$;
    }

    setEditingContact(id: string): void {
        this.updateEditingContactSubject.next(id);
    }

    private emitEditingContact(contact: ContactModel) {
        this.originalContact = contact.clone();
        this.editingContact$.next(contact);
        this.dirtyFlag = false;
        this.dirtyFlag$.next(this.dirtyFlag);
    }

    private getAndUpdateEditingContact(uuid: string): Observable<void> {
        return this.contactsPublicApi.getContact(uuid).pipe(
            switchMap(apiModel => from(this.journodexEntityFactory.createContact(apiModel))),
            switchMap(contact => {
                return this.targetingListsApiService.getListMemberships([{contactUuid: uuid}]).pipe(
                    map((listMemberships) => {
                        this.editingContact = contact;
                        this.editingContact.listMemberships = listMemberships[0]?.targetingLists?.map(x => new TargetingList(x)) || [];
                        this.emitEditingContact(this.editingContact);
                    }),
                );
            }),
        );
    }

    createContact(entity: ContactModel, listIds: number[], overlayMode: boolean): Observable<string> {
        return this.contactsPublicApi.createContact(entity.toApiModel(), listIds).pipe(
            mergeMap(contactId => this.contactsPublicApi.getContact(contactId)),
            switchMap(apiModel => {
                const contact = new ContactModel(apiModel);
                this.editingContact.id = contact.id;
                this.newPrivateContact = this.convertNewContactToTargetingResultModel(contact);
                this.newPrivateContact$.next(this.newPrivateContact);

                this.messageService.add({
                    severity : messageSeverityEnum.success,
                    summary: 'Discover.contactEditor.message.created.summary',
                    detail: this.localizationService.transform('Discover.contactEditor.message.created.detail', {
                        firstName: this.newPrivateContact.firstName,
                        lastName: this.newPrivateContact.lastName
                    })
                });

                if (!overlayMode) {
                    this.discoverRoutingService.gotoContactEditor(contact.id);
                }
                return contact.id;
            }),
            switchMap(() => {
                return this.targetingListService.refreshAllManual().pipe(
                    filter(x => !!x),
                    take(1),
                    map(x => null)
                );
            }),
            catchError(error => {
                this.messageService.add({
                    severity : messageSeverityEnum.error,
                    summary: 'Discover.contactEditor.errors.errorCreate'
                });

                throw new Error(error);
            })
        );
    }

    editContact(contact: ContactModel, listIds: number[]): Observable<void> {
        return this.contactsPublicApi.editContact(contact.toApiModel(), listIds).pipe(
            switchMap(() => this.getAndUpdateEditingContact(contact.id)),
            tap(() => {
                this.messageService.add({
                    severity : messageSeverityEnum.success,
                    summary: 'Discover.contactEditor.message.updated.summary',
                    detail: this.localizationService.transform('Discover.contactEditor.message.updated.detail',
                        { firstName: contact.firstName, lastName: contact.lastName })
                });
            }),
            switchMap(() => {
                return this.targetingListService.refreshAllManual().pipe(
                    filter(x => !!x),
                    take(1),
                    map(x => null)
                );
            }),
            catchError((error) => {
                this.messageService.add({
                    severity : messageSeverityEnum.error,
                    summary: 'Discover.contactEditor.errors.errorUpdate'
                });
                throw new Error(error);
            })
        );
    }

    deleteContacts(contacts: TargetingResultModel[], deleteAllMatchingSearch?: TargetingQueryApi, total?: number) {
        let heading;
        let message;
        let successMessageDetails;
        let successMessageSummary;

        let count = total;
        if (!deleteAllMatchingSearch) {
            count = contacts.length;
        }
        if (count === 1) {
            heading = 'Discover.contactEditor.deleteConfirm.header.one';
            message = 'Discover.contactEditor.deleteConfirm.message.one';
            successMessageSummary = 'Discover.contactEditor.message.deleted.one.summary';
            successMessageDetails = this.localizationService.transform(
                'Discover.contactEditor.message.deleted.one.detail',
                { name : contacts[0].contactName});
        } else {
            heading = 'Discover.contactEditor.deleteConfirm.header.many';
            message = this.localizationService.transform(
                'Discover.contactEditor.deleteConfirm.message.many',
                { count : count});
            successMessageSummary = 'Discover.contactEditor.message.deleted.many.summary';
            successMessageDetails = this.localizationService.transform(
                'Discover.contactEditor.message.deleted.many.detail',
                { count : count});
        }

        const confirmConfig: ConfirmDialogModel = {
            inputs: {
                id: 'PrivateContactDelete',
                heading: heading,
                messageHtml: message,
                okLabel: 'Monitoring.confirm'
            },
            outputs: {
                dialogClose: (confirmed: boolean) => {
                    if (!confirmed) {

                        return;
                    }
                    this.deleteLoading$.next(true);
                    let apiCall;
                    if (!!deleteAllMatchingSearch) {
                        apiCall = this.contactsPublicApi.deletePrivatesMatchingSearch(deleteAllMatchingSearch);
                    } else {
                        apiCall = this.contactsPublicApi.deleteContacts(contacts.map(x => x.id));
                    }
                    apiCall.pipe(
                        switchMap(() => {
                            return this.targetingListService.refreshAllManual().pipe(
                                filter(x => !!x),
                                take(1),
                                map(x => null)
                            );
                        }),
                        catchError(err => {
                            this.messageService.add({
                                severity: messageSeverityEnum.error,
                                summary: 'Discover.contactEditor.message.errorDelete',
                            });

                            throw err;
                        }),
                        tap(() => {
                            this.messageService.add({
                                severity: messageSeverityEnum.success,
                                summary: successMessageSummary,
                                detail: successMessageDetails
                            });
                        })
                    ).subscribe(() => {
                        this.deleteLoading$.next(false);
                        this.targetingSearchService.refreshResults();
                    });
                }
            }
        };
        this.coreDialogService.open(ConfirmDialogComponent, confirmConfig);
    }

    updateDirtyFlag() {
        this.dirtyFlag = !this.editingContact.equals(this.originalContact);
        this.dirtyFlag$.next(this.dirtyFlag);
    }

    getDirtyFlag(): Observable<boolean> {
        return this.dirtyFlag$;
    }

    getFocusedRhpEntity() {
        return this.rhpFocusedEntity$;
    }

    getProfileEntity() {
        return this.rhpProfileEntity$;
    }

    setRhpFocusedEntity(entity: TargetingResultModel, outletId: string = null) {
        this.rhpFocusedEntity = entity;
        this.rhpFocusedEntity$.next(this.rhpFocusedEntity);

        if (this.fullEntityRequest) {
            this.fullEntityRequest.unsubscribe();
            this.fullEntityRequest = null;
        }

        if (this.rhpFocusedEntity) {
            switch (this.rhpFocusedEntity.entityType) {
                case EntityType.CONTACT:

                    if (this.rhpProfileEntity && this.rhpProfileEntity.id === entity.id) {
                        if (outletId) {
                            this.selectOutletForProfile(outletId);
                        }
                        return;
                    }
                    this.fullEntityRequest = this.contactsPublicApi.getContact(this.rhpFocusedEntity.id).subscribe(
                        async contact => {
                            this.rhpProfileEntity = await this.journodexEntityFactory.createContact(contact);
                            this.rhpProfileEntity$.next(this.rhpProfileEntity);
                            this.fullEntityRequest = null;

                            this.rhpFocusedEntity.loadedEmployments = this.rhpProfileEntity.employmentModels;
                            this.rhpFocusedEntity.selectedEmployment = this.rhpFocusedEntity.loadedEmployments
                                .find(x => x.id === this.rhpFocusedEntity.selectedEmployment.id);
                            if (outletId) {
                                this.selectOutletForProfile(outletId);
                            }
                        }
                    );
                    break;
                case EntityType.OUTLET:
                    this.selectOutletForProfile(this.rhpFocusedEntity.id);
                    break;
                default:
                    console.error('Unexpected entity type for RHP focused entity');
            }

        }

    }

    selectOutletForProfile(outletId: string) {
        if (this.rhpProfileEntity) {
            this.rhpProfileEntity = null;
            this.rhpProfileEntity$.next(this.rhpProfileEntity);
        }
        this.fullEntityRequest = this.outletsPublicApi.getOutlet(outletId).subscribe(
            async outlet => {
                this.rhpProfileEntity = await this.journodexEntityFactory.createOutlet(outlet);
                this.rhpProfileEntity$.next(this.rhpProfileEntity);
                this.fullEntityRequest = null;
            }
        );
    }

    setPrivateDataAllLayerUpdate(update: PrivateDataAllLayerUpdate) {
        this.privateDataAllLayerUpdate = update;
        this.privateDataAllLayerUpdate$.next(this.privateDataAllLayerUpdate);
    }

    getPrivateDataAllLayerUpdate(): Observable<PrivateDataAllLayerUpdate> {
        return this.privateDataAllLayerUpdate$;
    }

    convertNewContactToTargetingResultModel(contact: ContactModel): TargetingResultModel {
        const result = this.targetingResultFactory.fromContactModel(contact);
        result.listMemberships = [];
        result.isPrivate = true;
        return result;
    }

    setNotesAllLayerUpdate(notesUpdate: NotesAllLayerUpdate) {
        this.notesAllLayerUpdate$.next(notesUpdate);
    }

    getNotesAllLayerUpdate(): Observable<NotesAllLayerUpdate> {
        return this.notesAllLayerUpdate$;
    }

    setHandlesAllLayerUpdate(handlesUpdate: HandlesAllLayerUpdate) {
        this.handlesAllLayerUpdate = handlesUpdate;
        this.handlesAllLayerUpdate$.next(this.handlesAllLayerUpdate);
    }

    getHandlesAllLayerUpdate(): Observable<HandlesAllLayerUpdate> {
        return this.handlesAllLayerUpdate$;
    }
}
